import { Head, router } from "@inertiajs/react"
import { Fragment, useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateImport } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface TimeEntry {
  id: number
  entry_date: string | null
  user_name: string | null
  service_type: string | null
  hours: number | null
  mileage: number
  employee_pay: number
  notes: string | null
  approval_notes: string | null
  show_url: string
  description: string | null
  warnings: string[]
  mileage_reimbursement: boolean
}

interface Expense {
  id: number
  expense_date: string | null
  user_name: string | null
  expense_type: string | null
  amount: number
  paid_to: string | null
  notes: string | null
  approval_notes: string | null
  show_url: string
  warnings: string[]
  receipt_attached: boolean
}

interface ReviewDecision {
  [key: string]: string
  action: string
  notes: string
}

interface Props {
  case_record: CaseRecord
  time_entries: TimeEntry[]
  expenses: Expense[]
}

export default function ManagementActivity({
  case_record,
  time_entries,
  expenses,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    { title: case_record.display_name, href: `/cases/${case_record.id}` },
    {
      title: "Activity Review",
      href: `/cases/${case_record.id}/management/activity`,
    },
  ]

  const [timeDecisions, setTimeDecisions] = useState<
    Record<string, ReviewDecision>
  >({})
  const [expenseDecisions, setExpenseDecisions] = useState<
    Record<string, ReviewDecision>
  >({})
  const [submitting, setSubmitting] = useState(false)

  const updateTimeDecision = (
    id: number,
    field: keyof ReviewDecision,
    value: string,
  ) => {
    setTimeDecisions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        action: prev[id]?.action || "",
        notes: prev[id]?.notes || "",
        [field]: value,
      },
    }))
  }

  const updateExpenseDecision = (
    id: number,
    field: keyof ReviewDecision,
    value: string,
  ) => {
    setExpenseDecisions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        action: prev[id]?.action || "",
        notes: prev[id]?.notes || "",
        [field]: value,
      },
    }))
  }

  const handleSubmit = () => {
    const hasDecisions =
      Object.values(timeDecisions).some((d) => d.action) ||
      Object.values(expenseDecisions).some((d) => d.action)

    if (!hasDecisions) {
      alert(
        "Please select at least one approve or deny action before submitting.",
      )
      return
    }

    setSubmitting(true)
    router.post(
      `/cases/${case_record.id}/management/activity/review`,
      {
        review: {
          time_entries: timeDecisions,
          expenses: expenseDecisions,
        },
      },
      {
        onFinish: () => setSubmitting(false),
      },
    )
  }

  const formatDate = (date: string | null) => {
    return formatDateImport(date) || "—"
  }

  const formatHours = (hours: number | null) => {
    if (!hours) return "—"
    const h = Math.floor(hours)
    const m = Math.round((hours - h) * 60)
    return `${h}:${m.toString().padStart(2, "0")}`
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const isEmpty = time_entries.length === 0 && expenses.length === 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Activity Review — ${case_record.display_name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-hpi-dark-gray text-xl font-semibold">
            Activity Review
          </h2>
          {!isEmpty && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          )}
        </div>

        {isEmpty && (
          <p className="text-hpi-med-blue text-sm">
            No pending time entries or expenses to review for this case.
          </p>
        )}

        {/* Time Entries Section */}
        {time_entries.length > 0 && (
          <section className="mb-8">
            <h3 className="text-hpi-dark-blue mb-3 text-lg font-semibold">
              Pending Time Entries ({time_entries.length})
            </h3>
            <div className="border-hpi-med-gray overflow-x-auto rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-hpi-dark-blue text-left text-white">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Employee</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Miles</th>
                    <th className="px-3 py-2 text-right">Pay</th>
                    <th className="px-3 py-2">Decision</th>
                    <th className="px-3 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {time_entries.map((entry, idx) => {
                    const bgClass =
                      idx % 2 === 0 ? "bg-white" : "bg-hpi-pale-blue"
                    const hasDetails =
                      (entry.warnings?.length ?? 0) > 0 ||
                      !!entry.description ||
                      !!entry.notes ||
                      !!entry.approval_notes ||
                      entry.mileage_reimbursement
                    return (
                      <Fragment key={entry.id}>
                        <tr className={bgClass}>
                          <td className="px-3 py-2">
                            <a
                              href={entry.show_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-hpi-active-blue hover:underline"
                            >
                              {formatDate(entry.entry_date)}
                            </a>
                          </td>
                          <td className="px-3 py-2">
                            <a
                              href={entry.show_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-hpi-active-blue hover:underline"
                            >
                              {entry.service_type ?? "—"}
                            </a>
                          </td>
                          <td className="px-3 py-2">
                            <a
                              href={entry.show_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-hpi-active-blue hover:underline"
                            >
                              {entry.user_name ?? "—"}
                            </a>
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {formatHours(entry.hours)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {entry.mileage || 0}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(entry.employee_pay)}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={timeDecisions[entry.id]?.action || ""}
                              onChange={(e) =>
                                updateTimeDecision(
                                  entry.id,
                                  "action",
                                  e.target.value,
                                )
                              }
                              className="border-hpi-med-gray w-full rounded border px-2 py-1 text-sm"
                            >
                              <option value="">Choose</option>
                              <option value="approve">Approve</option>
                              <option value="deny">Deny</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={timeDecisions[entry.id]?.notes || ""}
                              onChange={(e) =>
                                updateTimeDecision(
                                  entry.id,
                                  "notes",
                                  e.target.value,
                                )
                              }
                              placeholder="Review notes..."
                              className="border-hpi-med-gray w-full rounded border px-2 py-1 text-sm"
                            />
                          </td>
                        </tr>
                        {hasDetails && (
                          <tr className={bgClass}>
                            <td colSpan={8} className="px-3 pt-0 pb-2">
                              <div className="space-y-0.5 text-xs">
                                {entry.warnings?.map((w, i) => (
                                  <div
                                    key={i}
                                    className="text-hpi-red font-semibold"
                                  >
                                    {w}
                                  </div>
                                ))}
                                {entry.description && (
                                  <div className="text-hpi-dark-gray">
                                    {entry.description}
                                  </div>
                                )}
                                {entry.mileage_reimbursement && (
                                  <div className="font-medium text-green-600">
                                    $ Reimbursement requested
                                  </div>
                                )}
                                {entry.notes && (
                                  <div className="text-hpi-dark-gray">
                                    {entry.notes}
                                  </div>
                                )}
                                {entry.approval_notes && (
                                  <div className="text-hpi-red font-medium whitespace-pre-wrap">
                                    Notes: {entry.approval_notes}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Expenses Section */}
        {expenses.length > 0 && (
          <section className="mb-8">
            <h3 className="text-hpi-dark-blue mb-3 text-lg font-semibold">
              Pending Expenses ({expenses.length})
            </h3>
            <div className="border-hpi-med-gray overflow-x-auto rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-hpi-dark-blue text-left text-white">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Employee</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Decision</th>
                    <th className="px-3 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense, idx) => {
                    const bgClass =
                      idx % 2 === 0 ? "bg-white" : "bg-hpi-pale-blue"
                    const hasDetails =
                      (expense.warnings?.length ?? 0) > 0 ||
                      !!expense.paid_to ||
                      expense.receipt_attached ||
                      !!expense.notes ||
                      !!expense.approval_notes
                    return (
                      <Fragment key={expense.id}>
                        <tr className={bgClass}>
                          <td className="px-3 py-2">
                            <a
                              href={expense.show_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-hpi-active-blue hover:underline"
                            >
                              {formatDate(expense.expense_date)}
                            </a>
                          </td>
                          <td className="px-3 py-2">
                            <a
                              href={expense.show_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-hpi-active-blue hover:underline"
                            >
                              {expense.expense_type ?? "—"}
                            </a>
                          </td>
                          <td className="px-3 py-2">
                            <a
                              href={expense.show_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-hpi-active-blue hover:underline"
                            >
                              {expense.user_name ?? "—"}
                            </a>
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {formatCurrency(expense.amount)}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={expenseDecisions[expense.id]?.action || ""}
                              onChange={(e) =>
                                updateExpenseDecision(
                                  expense.id,
                                  "action",
                                  e.target.value,
                                )
                              }
                              className="border-hpi-med-gray w-full rounded border px-2 py-1 text-sm"
                            >
                              <option value="">Choose</option>
                              <option value="approve">Approve</option>
                              <option value="deny">Deny</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={expenseDecisions[expense.id]?.notes || ""}
                              onChange={(e) =>
                                updateExpenseDecision(
                                  expense.id,
                                  "notes",
                                  e.target.value,
                                )
                              }
                              placeholder="Review notes..."
                              className="border-hpi-med-gray w-full rounded border px-2 py-1 text-sm"
                            />
                          </td>
                        </tr>
                        {hasDetails && (
                          <tr className={bgClass}>
                            <td colSpan={6} className="px-3 pt-0 pb-2">
                              <div className="space-y-0.5 text-xs">
                                {expense.warnings?.map((w, i) => (
                                  <div
                                    key={i}
                                    className="text-hpi-red font-semibold"
                                  >
                                    {w}
                                  </div>
                                ))}
                                {expense.paid_to && (
                                  <div className="text-hpi-dark-gray">
                                    Paid to: {expense.paid_to}
                                  </div>
                                )}
                                {expense.receipt_attached && (
                                  <div className="font-medium text-green-600">
                                    Receipt attached
                                  </div>
                                )}
                                {expense.notes && (
                                  <div className="text-hpi-dark-gray">
                                    {expense.notes}
                                  </div>
                                )}
                                {expense.approval_notes && (
                                  <div className="text-hpi-red font-medium whitespace-pre-wrap">
                                    Notes: {expense.approval_notes}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {!isEmpty && (
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
